/**
 * 출석 코드 — 행사 QR 하나(awanaevent.com/go) + 교시 × 강의별 4자리 방 코드 (방법 3, 방법 2 도 선택 가능)
 */
import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { QRCodeSVG } from 'qrcode.react';
import {
  Alert, Box, Button, IconButton, MenuItem, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { ContentCopy, Delete, Refresh } from '@mui/icons-material';
import { api } from '../api';
import { C, todayYmd, ymdhm } from '../common';
import { Card, Empty } from './ui';

type Slot = {
  _id: string; eventDate: string; period: string; startAt: string; endAt: string; title: string; room?: string; instructor?: string;
  code: string; screenKey: string; active: boolean; count: number; eduSessionId?: string | null; btSessionId?: string;
};
const hm = (d: string) => ymdhm(d).slice(11);

const AttendanceTab: React.FC = () => {
  const [date, setDate] = useState(todayYmd());
  const [slots, setSlots] = useState<Slot[]>([]);
  const [dates, setDates] = useState<string[]>([]);
  const [method, setMethod] = useState<2 | 3>(3);
  const [form, setForm] = useState({ period: '1교시', start: '09:30', end: '10:20', title: '', room: '', instructor: '', eduSessionId: '' });
  const [eduSessions, setEduSessions] = useState<Array<{ _id: string; label: string }>>([]);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const origin = window.location.origin;
  const goUrl = `${origin}/go`;

  const load = () => api<{ data: { slots: Slot[]; dates: string[]; method: 2 | 3 } }>(`/admin/edu/attend/slots?date=${date}`)
    .then((r) => { setSlots(r.data.slots); setDates(r.data.dates); setMethod(r.data.method); }).catch((e) => setError(e.message));
  useEffect(() => { load(); const t = window.setInterval(load, 20000); return () => window.clearInterval(t); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      const cs = await api<{ data: Array<{ _id: string; code: string; mode: string }> }>('/admin/edu/courses');
      const out: Array<{ _id: string; label: string }> = [];
      for (const c of cs.data.filter((x) => x.mode === 'session' && x.code !== 'BT')) {
        const ss = await api<{ data: Array<{ _id: string; title: string }> }>(`/admin/edu/courses/${c._id}/sessions`);
        ss.data.forEach((s) => out.push({ _id: s._id, label: `${c.code} · ${s.title}` }));
      }
      setEduSessions(out);
    })().catch(() => undefined);
  }, []);

  const screenUrl = (s: Slot) => `${origin}/go/screen/${s._id}?k=${s.screenKey}`;
  const copy = (text: string, label: string) => navigator.clipboard.writeText(text).then(() => setMsg(`${label} 복사했습니다.`));
  const add = async () => {
    try {
      await api('/admin/edu/attend/slots', { body: { ...form, eventDate: date } });
      setForm({ ...form, title: '', room: '', instructor: '' }); load();
    } catch (e: any) { setError(e.message); }
  };
  const update = async (s: Slot, body: any) => { await api(`/admin/edu/attend/slots/${s._id}`, { method: 'PUT', body }).catch((e) => setError(e.message)); load(); };
  const remove = async (s: Slot) => {
    if (!window.confirm(`'${s.title}' 을(를) 삭제할까요?${s.count ? ` 출석 ${s.count}명 기록도 지워집니다.` : ''}`)) return;
    await api(`/admin/edu/attend/slots/${s._id}${s.count ? '?force=1' : ''}`, { method: 'DELETE' }).catch((e) => setError(e.message));
    load();
  };
  const setMethodSave = async (m: 2 | 3) => { setMethod(m); await api('/admin/cert/settings', { method: 'PUT', body: { attendanceMethod: m } }); };
  const exportRoster = async () => {
    const r = await api<{ data: any[] }>(`/admin/edu/attend/roster?date=${date}`);
    const ws = XLSX.utils.json_to_sheet(r.data.map((x) => ({ 날짜: x.date, 교시: x.period, 강의: x.title, 장소: x.room, 이름: x.teacherName, 휴대폰: x.teacherPhone, 교회: x.churchName, 출석시각: ymdhm(x.checkedAt) })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '출석');
    XLSX.writeFile(wb, `출석명단_${date}.xlsx`);
  };
  const periods = Array.from(new Set(slots.map((s) => s.period)));

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 300px' } }}>
        <Card title="출석 방식">
          <ToggleButtonGroup size="small" exclusive value={method} onChange={(_, v) => v && setMethodSave(v)}>
            <ToggleButton value={3}>3번 · 강의 선택 + 방 코드 (권장)</ToggleButton>
            <ToggleButton value={2}>2번 · 강의 선택만</ToggleButton>
          </ToggleButtonGroup>
          <Typography fontSize={13} color={C.sub} mt={1.5}>
            교사는 행사 QR 하나(또는 교사카드의 ‘출석하기’)로 접속 → 처음 한 번 이름·휴대폰 확인 → 지금 진행 중인 강의 목록에서 선택
            {method === 3 ? ' → 강사 화면의 4자리 코드 입력' : ''} → 출석 완료. 강의마다 QR을 따로 나눠줄 필요가 없습니다.
          </Typography>
          <Typography fontSize={13} color={C.sub} mt={0.5}>기존 방식(스태프가 교사 QR을 스캔)도 그대로 쓸 수 있습니다.</Typography>
        </Card>
        <Card title="행사 QR (모든 강의 공통)">
          <Box sx={{ display: 'grid', placeItems: 'center', gap: 1 }}>
            <QRCodeSVG value={goUrl} size={150} />
            <Typography fontWeight={800}>{goUrl.replace(/^https?:\/\//, '')}</Typography>
            <Button size="small" onClick={() => copy(goUrl, '주소를')}>주소 복사</Button>
          </Box>
        </Card>
      </Box>

      <Card title="교시 × 강의" action={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField type="date" size="small" value={date} onChange={(e) => setDate(e.target.value)} />
          {dates.length > 0 && <TextField select size="small" value="" label="다른 날" onChange={(e) => setDate(e.target.value)} sx={{ width: 120 }}>
            {dates.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>}
          <Button size="small" variant="outlined" disabled={!slots.length} onClick={() => copy(slots.map((s) => `[${s.period}] ${s.title}${s.room ? ` (${s.room})` : ''}\n${screenUrl(s)}`).join('\n\n'), '강사 화면 주소 전체를')}>강사 화면 주소 전체 복사</Button>
          <Button size="small" variant="outlined" disabled={!slots.length} onClick={exportRoster}>출석 명단 엑셀</Button>
        </Box>
      }>
        {msg && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setMsg('')}>{msg}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
        {!slots.length ? <Empty>{date} 강의가 없습니다. 아래에서 추가하세요.</Empty> : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow sx={{ '& th': { fontSize: 12.5, color: C.sub, fontWeight: 700, bgcolor: '#fffbea', whiteSpace: 'nowrap' } }}>
                <TableCell>교시</TableCell><TableCell>시간</TableCell><TableCell>강의</TableCell><TableCell>장소 · 강사</TableCell>
                {method === 3 && <TableCell>방 코드</TableCell>}<TableCell>출석</TableCell><TableCell>사용</TableCell><TableCell />
              </TableRow></TableHead>
              <TableBody>
                {periods.map((p) => slots.filter((s) => s.period === p).map((s, i) => (
                  <TableRow key={s._id} sx={{ opacity: s.active ? 1 : 0.5 }}>
                    <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{i === 0 ? p : ''}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>{hm(s.startAt)}–{hm(s.endAt)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{s.title}{s.eduSessionId && <Typography fontSize={11} color={C.sub}>교육 이력 연결</Typography>}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{[s.room, s.instructor].filter(Boolean).join(' · ')}</TableCell>
                    {method === 3 && <TableCell><Typography sx={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 800, letterSpacing: '.15em' }}>{s.code}</Typography></TableCell>}
                    <TableCell sx={{ fontWeight: 800 }}>{s.count}명</TableCell>
                    <TableCell><Switch size="small" checked={s.active} onChange={(e) => update(s, { active: e.target.checked })} /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="강사 화면 주소 복사"><IconButton size="small" onClick={() => copy(screenUrl(s), '강사 화면 주소를')}><ContentCopy fontSize="small" /></IconButton></Tooltip>
                      <Button size="small" onClick={() => window.open(screenUrl(s), '_blank')}>강사 화면</Button>
                      {method === 3 && <Tooltip title="코드 새로 만들기"><IconButton size="small" onClick={() => update(s, { regenerateCode: true })}><Refresh fontSize="small" /></IconButton></Tooltip>}
                      <IconButton size="small" onClick={() => remove(s)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                )))}
              </TableBody>
            </Table>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, p: 1.5, border: `1px dashed ${C.line}`, borderRadius: 2 }}>
          <TextField size="small" label="교시" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} sx={{ width: 90 }} />
          <TextField size="small" type="time" label="시작" InputLabelProps={{ shrink: true }} value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} sx={{ width: 120 }} />
          <TextField size="small" type="time" label="종료" InputLabelProps={{ shrink: true }} value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} sx={{ width: 120 }} />
          <TextField size="small" label="강의명" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} sx={{ flex: 1, minWidth: 180 }} />
          <TextField size="small" label="장소" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} sx={{ width: 120 }} />
          <TextField size="small" label="강사" value={form.instructor} onChange={(e) => setForm({ ...form, instructor: e.target.value })} sx={{ width: 110 }} />
          {eduSessions.length > 0 && (
            <TextField select size="small" label="교육 이력 연결 (선택)" value={form.eduSessionId} onChange={(e) => setForm({ ...form, eduSessionId: e.target.value })} sx={{ width: 220 }}>
              <MenuItem value="">연결 안 함</MenuItem>{eduSessions.map((s) => <MenuItem key={s._id} value={s._id}>{s.label}</MenuItem>)}
            </TextField>
          )}
          <Button variant="contained" disabled={!form.title} onClick={add}>추가</Button>
        </Box>
      </Card>
    </Box>
  );
};

export default AttendanceTab;
